/**
 * The parent lock PIN, and the refresh token it keeps.
 *
 * WHAT THIS CHANGES, stated plainly because it reverses a decision that was
 * argued for at length. DECISIONS.md §19 put the refresh token in
 * `sessionStorage` -- per tab, gone when the tab closes -- and called that the
 * control keeping a child out of a parent's account on the shared family
 * computer. It also admitted the gap: a parent who leaves a tab open and walks
 * away is protected by nothing at all, and closing that "needs an idle lock,
 * which is a separate change and a separate decision". This is that change.
 *
 * A six-digit PIN is a WEAKER secret than a Cognito password, and pretending
 * otherwise would be the wrong way to build this. Three things carry it.
 *
 * 1. THE TOKEN IS NEVER STORED IN THE CLEAR. What goes in `localStorage` is
 *    AES-GCM ciphertext under a key derived from the PIN with 600,000 rounds
 *    of PBKDF2-SHA256 -- the same cost the family key pays in familyKey.ts.
 *    A child who opens devtools and reads the record gets bytes.
 *
 * 2. GUESSING IS CAPPED, AND THE COUNT IS WRITTEN BEFORE THE ATTEMPT. Five
 *    wrong PINs delete the record, and the only way back is a full Cognito
 *    sign-in. The write ordering is the load-bearing part: the counter is
 *    persisted with a SYNCHRONOUS `localStorage.setItem` before the async
 *    decrypt begins, so closing the tab mid-guess does not roll it back. Get
 *    that backwards and the cap is decorative -- guess, kill the tab, repeat.
 *
 * 3. THE RECORD DOES NOT TRAVEL. The PIN alone cannot open it. The key is
 *    derived from the PIN *and* a non-extractable HMAC key held in IndexedDB,
 *    so the ciphertext copied off this machine is not brute-forceable
 *    anywhere else -- 10^6 candidates is minutes of offline GPU work, and
 *    this is what stops that being possible at all. Script running on this
 *    origin can still *use* that HMAC key; it cannot take it away, which is
 *    the difference between an attack that needs this browser and one that
 *    does not.
 *
 * The net effect on the threat §19 cares about -- a child opening the browser
 * later -- is unchanged: they meet a PIN prompt instead of a password prompt,
 * and they know neither. The effect on the gap §19 admits is a real
 * improvement, because `lock()` can now end a session without ending the
 * parent's ability to get back into it in four seconds.
 *
 * WHAT IS DELIBERATELY NOT HERE: any server-side record of the PIN. It is
 * never sent anywhere, never hashed into anything that leaves the device, and
 * a parent who forgets it is not recovering it -- they sign in with Cognito
 * and set one again, which may be the same six digits. That is why there is
 * no "reset" call in this file. Signing in IS the reset.
 */

import { fromBase64, toBase64 } from '../crypto/familyKey';

/** Matches familyKey.ts. OWASP's PBKDF2-HMAC-SHA256 floor. */
const KDF_ITERATIONS = 600_000;
const SALT_BYTES = 16;
const NONCE_BYTES = 12;

/** Six digits. Not four: 10^4 is small enough that the cap is the only guard. */
export const PIN_LENGTH = 6;

/**
 * Wrong answers allowed before the record is destroyed.
 *
 * Five, because a PIN is typed by a tired adult on a phone and one fat-finger
 * should not cost a full sign-in, while five wrong guesses out of a million
 * candidates is not an attack that gets anywhere.
 */
export const MAX_ATTEMPTS = 5;

const RECORDS_KEY = 'valley.auth.locked';
const DB_NAME = 'valley-auth';
const DB_VERSION = 1;
const STORE = 'device';
const DEVICE_KEY_ID = 'pin-pepper';

/**
 * One parent's locked session, as it sits in localStorage.
 *
 * The email is here in the clear and that is a deliberate, narrow choice: the
 * account picker has to show a parent which account they are unlocking before
 * they can type the PIN that would decrypt anything. An attacker reading this
 * learns which adults use this browser. They already learn that from the
 * child sessions beside it, and from the browser's own history.
 */
export interface LockedAccount {
  sub: string;
  email: string;
  /** Base64. PBKDF2 salt, and the HMAC input that pins this to this device. */
  salt: string;
  /** Base64. AES-GCM nonce, fresh on every write. */
  nonce: string;
  /** Base64. The refresh token, encrypted. */
  ciphertext: string;
  /** Stored so raising the floor later can still open records written today. */
  iterations: number;
  /** Wrong PINs since the last success. Reaching MAX_ATTEMPTS destroys this. */
  attempts: number;
  /** Epoch ms, for "last used" ordering in the picker. */
  savedAt: number;
}

function readAll(): LockedAccount[] {
  try {
    const raw = localStorage.getItem(RECORDS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as LockedAccount[]) : [];
  } catch {
    // Private mode, blocked site data, or something else's key at this name.
    // No locked accounts is a correct answer to every caller here.
    return [];
  }
}

function writeAll(records: LockedAccount[]): void {
  try {
    localStorage.setItem(RECORDS_KEY, JSON.stringify(records));
  } catch {
    // Nothing to do. A PIN that cannot be saved surfaces as the account not
    // appearing in the picker, which is the honest outcome.
  }
}

/** Every account locked on this browser, most recently used first. */
export function lockedAccounts(): LockedAccount[] {
  return readAll().sort((a, b) => b.savedAt - a.savedAt);
}

export function lockedAccount(sub: string): LockedAccount | null {
  return readAll().find((one) => one.sub === sub) ?? null;
}

export function hasPin(sub: string): boolean {
  return lockedAccount(sub) !== null;
}

/** Drop one account's lock. Used by "sign out", and by five wrong PINs. */
export function forgetPin(sub: string): void {
  writeAll(readAll().filter((one) => one.sub !== sub));
}

/** Every lock on this browser, dropped. For "forget this device". */
export function forgetAllPins(): void {
  try {
    localStorage.removeItem(RECORDS_KEY);
  } catch {
    /* as above */
  }
}

// ---------------------------------------------------------------------------
// The device half of the key
// ---------------------------------------------------------------------------

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open storage.'));
  });
}

function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = work(tx.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('Storage failed.'));
        tx.oncomplete = () => db.close();
      }),
  );
}

/**
 * A per-browser HMAC key, created once and NEVER extractable.
 *
 * `extractable: false` is the whole point. IndexedDB structured-clones a
 * CryptoKey, so this survives restarts without its bytes ever existing as a
 * string -- and `exportKey` on it throws, for this app and for anything else
 * running on this origin. The ciphertext in localStorage is therefore useless
 * on any other machine, which is what makes a six-digit secret defensible.
 *
 * Note what this does NOT claim. Script on this origin can call `sign` with
 * this key just as this module does, so it is not an XSS defence -- see the
 * same honest caveat in crypto/vault.ts. It defends against the record being
 * COPIED somewhere with time and a GPU.
 */
async function devicePepperKey(): Promise<CryptoKey | null> {
  try {
    const existing = await run<CryptoKey | undefined>('readonly', (s) => s.get(DEVICE_KEY_ID));
    if (existing) return existing;
    const fresh = await crypto.subtle.generateKey(
      { name: 'HMAC', hash: 'SHA-256', length: 256 },
      false, // not extractable, and this is the line that matters
      ['sign'],
    );
    await run('readwrite', (s) => s.put(fresh, DEVICE_KEY_ID));
    return fresh;
  } catch {
    // A browser with IndexedDB turned off. The PIN still works; it is simply
    // no longer bound to this device. Degrading to "as strong as the PIN" is
    // better than refusing to have a PIN at all, and `boundToDevice` below
    // lets the UI say so rather than quietly pretending.
    return null;
  }
}

/** Whether the lock on this browser gets the device-binding half. */
export async function boundToDevice(): Promise<boolean> {
  return (await devicePepperKey()) !== null;
}

/**
 * PIN + device pepper -> an AES-GCM key.
 *
 * The pepper is derived from the SALT, not a constant, so two accounts on the
 * same browser never derive the same key even from the same six digits -- two
 * parents choosing 123456 is not a hypothetical.
 */
async function derive(
  pin: string,
  salt: Uint8Array,
  iterations: number,
): Promise<CryptoKey> {
  const pepperKey = await devicePepperKey();
  let material = pin;
  if (pepperKey) {
    const pepper = await crypto.subtle.sign('HMAC', pepperKey, salt as BufferSource);
    material = `${pin}:${toBase64(pepper)}`;
  }

  const base = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(material) as BufferSource,
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    // Both, because the same key re-seals rotated tokens for as long as this
    // session lasts. See `Sealer`.
    ['encrypt', 'decrypt'],
  );
}

/**
 * The right to re-seal this account, held for the length of a session.
 *
 * Rotation is enabled on the app client, so every refresh spends the stored
 * token and hands back a replacement. Something has to write that replacement
 * down or the next unlock opens a token Cognito has already retired -- and
 * the obvious way to manage that, keeping the PIN in a module variable, means
 * six digits sitting in memory for an hour.
 *
 * So what is kept is the DERIVED KEY, not the PIN: a non-extractable
 * `CryptoKey` that can re-seal and open this one account and do nothing else.
 * It costs no PBKDF2 either, which matters -- 600,000 rounds is close to a
 * second on a phone, and paying it on every token refresh would be felt.
 *
 * The salt is reused deliberately; the NONCE is fresh on every write, which
 * is the requirement AES-GCM actually has.
 */
export interface Sealer {
  reseal(refreshToken: string): Promise<void>;
}

function sealerFor(
  account: { sub: string; email: string },
  salt: Uint8Array,
  key: CryptoKey,
  iterations: number,
): Sealer {
  return {
    async reseal(refreshToken: string): Promise<void> {
      const nonce = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
      const ciphertext = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: nonce as BufferSource },
        key,
        new TextEncoder().encode(refreshToken) as BufferSource,
      );
      const record: LockedAccount = {
        sub: account.sub,
        email: account.email,
        salt: toBase64(salt),
        nonce: toBase64(nonce),
        ciphertext: toBase64(ciphertext),
        iterations,
        attempts: 0,
        savedAt: Date.now(),
      };
      writeAll([...readAll().filter((one) => one.sub !== account.sub), record]);
    },
  };
}

// ---------------------------------------------------------------------------
// Setting, and opening
// ---------------------------------------------------------------------------

export function isWellFormed(pin: string): boolean {
  return new RegExp(`^\\d{${PIN_LENGTH}}$`).test(pin);
}

/**
 * Digits that are not a PIN so much as a placeholder.
 *
 * Refused rather than warned about. A parent picking 000000 has not made a
 * choice the cap can protect -- six of the commonest sequences cover a large
 * share of real-world PIN choices, and an attacker gets five guesses.
 */
const TOO_OBVIOUS = new Set([
  '000000', '111111', '222222', '333333', '444444', '555555',
  '666666', '777777', '888888', '999999', '123456', '654321',
  '012345', '543210', '121212', '123123', '112233', '696969',
]);

export function isTooObvious(pin: string): boolean {
  return TOO_OBVIOUS.has(pin);
}

/**
 * Lock a refresh token under a PIN, and remember the account.
 *
 * Called when a parent sets a PIN, and again on every rotation while they are
 * unlocked -- see `relock`. Replaces any previous record for the same `sub`,
 * which is what makes "change my PIN" nothing more than setting it again.
 */
export async function setPin(
  pin: string,
  account: { sub: string; email: string },
  refreshToken: string,
): Promise<Sealer> {
  if (!isWellFormed(pin)) throw new Error(`A PIN is ${PIN_LENGTH} digits.`);

  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const key = await derive(pin, salt, KDF_ITERATIONS);
  const sealer = sealerFor(account, salt, key, KDF_ITERATIONS);
  await sealer.reseal(refreshToken);
  return sealer;
}

export type UnlockOutcome =
  | { status: 'ok'; refreshToken: string; record: LockedAccount; sealer: Sealer }
  /** The PIN was wrong. `left` is how many tries remain before the wipe. */
  | { status: 'wrong'; left: number }
  /** Too many wrong PINs, or no record. Only a full sign-in gets back in. */
  | { status: 'gone' };

/**
 * Open a locked account with a PIN.
 *
 * The AES-GCM tag is what tells a wrong PIN from a right one: a bad key fails
 * authentication and `decrypt` throws. There is no separate verifier to check
 * and therefore nothing an attacker can test a guess against offline without
 * also having the device pepper.
 *
 * ATTEMPT ACCOUNTING, and why it reads oddly. The failure is recorded BEFORE
 * the work, not after, because `decrypt` is async and a tab closed while it
 * runs would otherwise cost nothing. So the count goes up, the attempt
 * happens, and success puts it back to zero.
 */
export async function unlockWithPin(sub: string, pin: string): Promise<UnlockOutcome> {
  const record = lockedAccount(sub);
  if (!record) return { status: 'gone' };
  if (!isWellFormed(pin)) {
    // Not counted. This is a typo in a six-box field, not a guess, and the UI
    // does not submit until six digits are present anyway.
    return { status: 'wrong', left: MAX_ATTEMPTS - record.attempts };
  }

  // Spend the attempt first. Synchronous, and on disk before the await below.
  const spent = { ...record, attempts: record.attempts + 1 };
  if (spent.attempts >= MAX_ATTEMPTS) {
    // The last permitted guess. Whether or not it is right, this record is
    // not surviving a wrong one -- so remove it now and put it back on
    // success. A tab closed mid-verify then costs the record, which is the
    // safe direction for that race to fall.
    forgetPin(sub);
  } else {
    writeAll([...readAll().filter((one) => one.sub !== sub), spent]);
  }

  const salt = fromBase64(record.salt);
  try {
    const key = await derive(pin, salt, record.iterations);
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromBase64(record.nonce) as BufferSource },
      key,
      fromBase64(record.ciphertext) as BufferSource,
    );
    // Right PIN: put the record back the way it was, attempts cleared, and
    // freshly stamped so the picker shows this account first next time.
    const cleared = { ...record, attempts: 0, savedAt: Date.now() };
    writeAll([...readAll().filter((one) => one.sub !== sub), cleared]);
    return {
      status: 'ok',
      refreshToken: new TextDecoder().decode(plain),
      record: cleared,
      sealer: sealerFor(record, salt, key, record.iterations),
    };
  } catch {
    if (spent.attempts >= MAX_ATTEMPTS) return { status: 'gone' };
    return { status: 'wrong', left: MAX_ATTEMPTS - spent.attempts };
  }
}
